import React, { useEffect, useState } from 'react';

import { FormattedMessage } from '../../../../util/reactIntl';
import { fetchDiamondSponsors } from '../../../../util/api';

import css from './FooterDiamondSponsors.module.css';

/**
 * Fisher–Yates shuffle (copy). Used so each page load shows sponsors in a
 * fresh order without favoring AMS response order.
 *
 * @template T
 * @param {T[]} items
 * @returns {T[]}
 */
export const shuffleSponsors = items => {
  const next = Array.isArray(items) ? [...items] : [];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = next[i];
    next[i] = next[j];
    next[j] = tmp;
  }
  return next;
};

/**
 * Accessible name for a linked sponsor logo (hostname when available).
 *
 * @param {string} targetUrl
 * @returns {string}
 */
export const sponsorLinkLabel = targetUrl => {
  try {
    const host = new URL(targetUrl).hostname.replace(/^www\./i, '');
    return host ? `Diamond sponsor: ${host}` : 'Diamond sponsor';
  } catch (_) {
    return 'Diamond sponsor';
  }
};

/**
 * Client-only Diamond Sponsors row for the footer brand column.
 * Fetches active AMS logos via the marketplace proxy; shuffles order on each
 * load; renders nothing while loading or when the list is empty/errors.
 *
 * @component
 * @returns {JSX.Element|null}
 */
const FooterDiamondSponsors = () => {
  const [sponsors, setSponsors] = useState(null);

  useEffect(() => {
    let cancelled = false;

    fetchDiamondSponsors()
      .then(data => {
        if (cancelled) {
          return;
        }
        const list = Array.isArray(data?.sponsors) ? data.sponsors : [];
        setSponsors(shuffleSponsors(list));
      })
      .catch(() => {
        if (!cancelled) {
          setSponsors([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!sponsors || sponsors.length === 0) {
    return null;
  }

  return (
    <div className={css.root}>
      <p className={css.label}>
        <FormattedMessage id="Footer.diamondSponsors" />
      </p>
      <ul className={css.list}>
        {sponsors.map((sponsor, index) => {
          const image = (
            <img
              className={css.logo}
              src={sponsor.imageUrl}
              alt=""
              loading="lazy"
              decoding="async"
            />
          );
          const key = `${sponsor.imageUrl}-${index}`;

          return (
            <li key={key} className={css.item}>
              {sponsor.targetUrl ? (
                <a
                  href={sponsor.targetUrl}
                  className={css.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={sponsorLinkLabel(sponsor.targetUrl)}
                >
                  {image}
                </a>
              ) : (
                image
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default FooterDiamondSponsors;
